import { useState, useEffect } from "react";
import { Plus, Shield, Trash2 } from "lucide-react";

export default function IPWhitelistingCard({ className, onChange, initial = {} }) {
  const [enabled, setEnabled] = useState(initial.enabled ?? true);
  const [ip, setIp] = useState("");
  const [ipList, setIpList] = useState(initial.ipList ?? []);
  const [ipError, setIpError] = useState("");

  const isValidIp = (value) => {
    if (!value) return false;
    const v = value.trim();
    // IPv4
    const ipv4 = /^((25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(25[0-5]|2[0-4]\d|1?\d?\d)$/;
    // Basic IPv6 (covers full and many compressed forms)
    const ipv6 = /^(?:[A-Fa-f0-9]{1,4}(?::|$)){2,8}$/;
    return ipv4.test(v) || (v.includes(":") && ipv6.test(v));
  };

  const addIP = () => {
    const value = ip.trim();
    if (!value) {
      setIpError("Please enter an IP address");
      return;
    }

    if (!isValidIp(value)) {
      setIpError("Invalid IP address");
      return;
    }

    // Prevent duplicates
    if (ipList.includes(value)) {
      setIpError("IP address already added");
      return;
    }

    setIpList([...ipList, value]);
    setIp("");
    setIpError("");
  };

  const removeIP = (index) => {
    setIpList(ipList.filter((_, i) => i !== index));
  };

  // notify parent when enabled/ipList change
  useEffect(() => {
    if (onChange) onChange({ enabled, ipList });
  }, [enabled, ipList]);

  return (
    <div className={`w-full p-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 mt-6 ${className}`}
    >

      {/* Header row */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-blue-600" />
            <h2 className="text-xs font-semibold text-slate-800 dark:text-slate-100">
              IP Whitelisting
            </h2>
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Restrict callback access to specific IP addresses only
          </p>
        </div>

        {/* Switch */}
        <button
          type="button"
          onClick={() => setEnabled(!enabled)}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300
            ${enabled ? "bg-blue-600" : "bg-slate-300"}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300
              ${enabled ? "translate-x-4" : "translate-x-1"}`}
          />
        </button>
      </div>

      {/* Input + Add - hidden when disabled */}
      {enabled && (
        <div className="mt-6">
          <label className="text-sm font-sans mt-4 dark:text-slate-200 text-slate-800">
            Allowed IP Addresses
          </label>

          <div className="flex items-center w-1/2 gap-2 justify-around">
            <input
              type="text"
              value={ip}
              onChange={(e) => {
                setIp(e.target.value);
                if (ipError) setIpError("");
              }}
              placeholder="Enter IP address (e.g., 192.168.0.1)"
              className={`w-full px-2 py-2  bg-slate-100 dark:bg-slate-800 rounded-md 
                  mt-2 text-slate-900 dark:text-white text-xs placeholder-slate-500 focus:outline-none 
                  focus:ring-2 focus:ring-blue-500 transition-all border ${ipError ? 'border-red-400' : 'border-slate-200'} dark:border-slate-700`}
            />

            <button
              onClick={addIP}
              disabled={!ip.trim()}
              className={`w-10 h-8 mt-2 rounded-lg border border-slate-200 flex items-center justify-around font-semibold text-white transition ${!ip.trim() ? 'bg-slate-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-400'}`}
            >
              <Plus />
            </button>
          </div>

          {ipError && (
            <p className="text-red-500 text-xs mt-2">{ipError}</p>
          )}

          {/* Pills */}
          <div className="flex flex-wrap gap-2 mt-4">
            {ipList.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-blue-100 text-blue-700 
                   py-1 px-4 rounded-full text-sm"
              >
                {item}
                <Trash2
                  size={15}
                  className="cursor-pointer"
                  onClick={() => removeIP(index)}
                />
              </div>
            ))}
          </div>

          {/* No IPs Message */}
          {ipList.length === 0 && (
            <p className="text-[10px] text-slate-400 mt-3">
              No IP addresses added yet. Add at least one IP address for whitelisting.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
